import { Button, Checkbox, Col, Form, Input, Row, Select } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Panel from '../../../../shared/components/card/Panel';
import PageHeader from '../../../../shared/components/pageHeader/PageHeader';
import { APPLICATION_ID, GUTTOR_SPACE, GUTTOR_SPACE_SMALL, INVENTORY_MESSAGE_CONSTANTS, INVENTROY_CONSTANTS, REQUEST_PAYLOAD_FOR_ROOM } from '../../../../shared/constants/commonConstants';
import { FETCH_INPATIENT_PROCEDURE_LIST, INVENTORY_SAVE_IP_PACKAGE } from '../../../../shared/constants/urlEndPoints';
import {
  FetchInPatientProcedureResponse, SaveInclusionListDTO, SaveInPatientPackageDTO,
  SaveInPatientPackageRequest, SaveInPatientPackageResponse, SavePricingListDTO,
} from '../../../../shared/models/inPatient.model';
import { LookUpDetail } from '../../../../shared/models/lookUp.model';
import SendRequest from '../../../../shared/services/sendRequest.service';
import { fetchHospitalProfile } from '../../../../store/actions/hospitalAction';
import { fetchIPDepartmentMaster, fetchIPInclusionMaster } from '../../../../store/actions/inventoryAction';
import {
  base64UrlEncode, CalculateDiscountPercentage, CalculateFinalPrice, error,
  extractSecondLevelBaseUrl, getBulletPoints, GetFirstElement, replaceRsAndComma, success,
} from '../../../../utils/utility';
import './IPPackageDetail.less';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, convertFromHTML, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import '../../../../shared/components/editor/HtmlEditor.less';
import ApplicationDetail from '../../../../shared/services/applicationDetail.service';
import { InfoCircleOutlined } from '@ant-design/icons';
import NumberFormat from 'react-number-format';

const IPPackageForm = ({ backUrl, pageTitle, data, path }: any): ReactElement => {
  const [packageForm] = Form.useForm();
  const dispatch = useDispatch();
  const [procedureList, setProcedureList] = useState(null as any);
  const history = useHistory();
  const [editorHtmlText, setEditorHtmlText] = useState('<p></p>');
  const [contentState, setContentState] = useState(null as any);
  const [discount, setDiscount] = useState(0);
  const storedHospitalProfile = useSelector(
    ({ hospital: { hospitalProfile } }: any): any => hospitalProfile,
  );
  const inclusionMasterList = useSelector(
    ({ inventory: { inPatientinclusionMaster } }: any): any => inPatientinclusionMaster,
  );
  const departmentMasterList = useSelector(
    ({ inventory: { inPatientdepartmentMaster } }: any): any => inPatientdepartmentMaster,
  );
  useEffect((): void => {
    if (!inclusionMasterList) {
      dispatch(fetchIPInclusionMaster());
    }
    if (!departmentMasterList) {
      dispatch(fetchIPDepartmentMaster());
    }
    if (!storedHospitalProfile) {
      dispatch(fetchHospitalProfile(REQUEST_PAYLOAD_FOR_ROOM));
    }
  }, []);
  useEffect((): void => {
    if (storedHospitalProfile) {
      setInitialFields();
    }
  }, [data, storedHospitalProfile]);
  const setInitialFields = (): void => {
    if (data?.description) {
      setEditorHtmlText(data?.description);
      const contentBlocks = convertFromHTML(data?.description);
      // tslint:disable-next-line: no-shadowed-variable
      const contentState = ContentState.createFromBlockArray(contentBlocks?.contentBlocks);
      const raw = convertToRaw(contentState);
      setContentState(raw);
    }
    packageForm.setFieldsValue({
      packageDescription: editorHtmlText,
      department: data?.departmentName,
      procedure: data?.procedureName,
      lengthOfStay: data?.lengthOfStay,
      pricingList: fetchPricing(data?.pricingDetails),
      inclusions: data?.inclusions
        ?.filter((x: any): boolean => x.isActive)
        .map((x: any): string => {
          return x.inclusionName;
        }),
      discountPercent: discount,
    });
  };

  const fetchPricing = (pricingDetails: any): any => {
    const rooms = GetFirstElement(storedHospitalProfile)?.rooms || [];
    const distinctRooms = Array.from(new Set(rooms.map((x: any): any => x.typeId)))
      .map((id: any): any => {
        return {
          id,
          name: rooms.find((x: any): boolean => x.typeId === id).typeName,
        };
      });
    return distinctRooms.map((y: any): any => {
      const originalPrice = pricingDetails?.find((x: any): boolean => y.id === x.typeId)?.price;
      const discountedPrice = pricingDetails?.find((x: any): boolean => y.id === x.typeId)?.discountedPrice;
      return {
        id: y.id,
        roomType: y.name,
        price: originalPrice,
        discountPercent: CalculateDiscountPercentage(originalPrice, discountedPrice),
        finalPrice: discountedPrice,
        rooms: rooms?.filter((x: any): any => x.typeId === y.id)
          ?.map((x: any): any => x.roomAlias),
      };
    });
  };
  useEffect((): void => {
    if (storedHospitalProfile) {
      isRoomsConfigured();
    }
  }, [storedHospitalProfile]);

  const isRoomsConfigured = (): any => {
    const profile: any = GetFirstElement(storedHospitalProfile);
    if (profile && (!profile?.rooms || profile?.rooms?.length === 0)) {
      error(INVENTORY_MESSAGE_CONSTANTS.roomsNotConfigured);
    }
  };
  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  const onDepartmentChange = async (department: string): Promise<void> => {
    packageForm.setFieldsValue({
      procedure: '',
    });
    const depId = departmentMasterList?.find((x: LookUpDetail): boolean => {
      return x.value === department;
    })?.id;
    if (depId > 0) {
      fetchProcedureList(depId);
    }
  };

  const fetchProcedureList = async (depId: number): Promise<void> => {
    if (depId <= 0) {
      return;
    }
    const url = `${process.env.REACT_APP_INVENTORY_API_BASE_URL + FETCH_INPATIENT_PROCEDURE_LIST + depId}`;
    const result: any = await SendRequest.instance.get<FetchInPatientProcedureResponse>(url);
    if (result && result?.isSuccess) {
      setProcedureList(result?.procedureList?.map((x: any): any => {
        return {
          id: x.id,
          name: x.name,
          value: x.name,
        };
      }));
    }
  };

  const onProcedureChange = async (): Promise<void> => {
    if (!procedureList || procedureList?.length === 0) {
      const depId = departmentMasterList?.find((x: LookUpDetail): boolean => {
        return x.value === packageForm?.getFieldValue('department');
      })?.id;
      if (depId > 0) {
        fetchProcedureList(depId);
      }
    }
  };

  const onDiscountPercentChange = async (discountPercent: any, index: number): Promise<void> => {
    discountPercent = discountPercent === undefined ? 0 : discountPercent;
    const pricing = packageForm?.getFieldValue('pricingList');
    if (pricing && pricing[index]) {
      pricing[index].discountPercent = discountPercent;
      pricing[index].finalPrice = CalculateFinalPrice(Number(pricing[index].price || 0), Number(discountPercent || 0));
      packageForm?.setFieldsValue({ pricingList: pricing });
    }
  };

  const onPriceChange = async (price: any, index: number): Promise<void> => {
    price = price === undefined ? 0 : price;
    const pricing = packageForm?.getFieldValue('pricingList');
    if (pricing && pricing[index]) {
      pricing[index].price = price;
      pricing[index].finalPrice = CalculateFinalPrice(Number(price || 0), Number(pricing[index].discountPercent || 0));
      packageForm?.setFieldsValue({ pricingList: pricing });
    }
  };

  const onFormSubmit = async (): Promise<void> => {
    const validationError = await packageForm?.validateFields();
    if (validationError?.errorFields && validationError?.errorFields?.length > 0) {
      return;
    }
    savePackage();
  };

  const savePackage = async (isRemovePackage: boolean = false): Promise<void> => {
    const contentBlocks = convertFromHTML(editorHtmlText);
    const contentText = ContentState.createFromBlockArray(contentBlocks?.contentBlocks);
    const raw = convertToRaw(contentText);
    if (editorHtmlText === null || editorHtmlText === '' || editorHtmlText === undefined
      || raw.blocks.length === 0 || raw.blocks[0].text?.trim().length === 0) {
      error(INVENTORY_MESSAGE_CONSTANTS.enterDescription);
      return;
    }

    const packageDetails = packageForm?.getFieldsValue();
    const lengthOfStay = replaceRsAndComma(packageDetails?.lengthOfStay);
    const ipPackage: SaveInPatientPackageDTO = {
      id: data?.id,
      profileId: data?.profileId || ApplicationDetail.instance.getProfileId(),
      statusName: data?.statusName || INVENTROY_CONSTANTS.statusType_Submit,
      packageType: INVENTROY_CONSTANTS.packageType_InPatient,
      description: editorHtmlText,
      lengthOfStay: !isNaN(lengthOfStay as any) ? parseInt(lengthOfStay, 10) : 0,
      isActive: !isRemovePackage,
      procedureName: packageDetails?.procedure,
      procedureId: procedureList ? procedureList.find((x: any): any => x.name === packageDetails?.procedure)?.id : data?.procedureId,
      departmentId: departmentMasterList?.find((x: any): any => x.value === packageDetails?.department)?.id,
      pricingList: packageDetails?.pricingList?.map((pricing: any): SavePricingListDTO => {
        const savedPricingDetail = data?.pricingDetails?.find((x: any): any => x.typeId === pricing?.id);
        return {
          id: savedPricingDetail?.id,
          isActive: true,
          typeId: pricing?.id,
          price: pricing?.price,
          discountedPrice: pricing?.finalPrice,
          packageId: data?.id,
          statusName: data?.statusName || INVENTROY_CONSTANTS.statusType_Submit,
        };
      }),
    };
    const inclusionList: SaveInclusionListDTO[] = data?.inclusions?.map((inclusion: any): SaveInclusionListDTO => {
      let inc: SaveInclusionListDTO = {
        id: inclusion?.id,
        packageId: inclusion?.packageId,
        statusName: inclusion?.statusName,
        inclusionId: inclusion?.inclusionId,
        isActive: inclusion?.isActive,
      };
      if (!packageDetails?.inclusions?.includes(inclusion?.inclusionName)) {
        inc = { ...inc, isActive: false };
      }
      return inc;
    }) || [];
    packageDetails?.inclusions?.forEach((inclusion: string): void => {
      if (!data || !data.inclusions?.some((x: any): any => x.inclusionName === inclusion)) {
        inclusionList.push({
          id: 0,
          packageId: data?.id,
          statusName: data?.statusName || INVENTROY_CONSTANTS.statusType_Submit,
          isActive: true,
          inclusionId: inclusionMasterList?.find((x: any): any => x.value === inclusion)?.id,
        });
      }
    });
    ipPackage.inclusionList = inclusionList;
    const payload: SaveInPatientPackageRequest = {
      package: ipPackage,
      applicationId: APPLICATION_ID,
    };
    const url = process.env.REACT_APP_INVENTORY_API_BASE_URL + INVENTORY_SAVE_IP_PACKAGE;
    const result: SaveInPatientPackageResponse = await SendRequest.instance.post<
      SaveInPatientPackageRequest,
      SaveInPatientPackageResponse
    >(url, payload);
    if (result && result?.isSuccess) {
      success(INVENTORY_MESSAGE_CONSTANTS.packageSaved);
      if (result?.ids && result?.ids.length > 0) {
        history.push(extractSecondLevelBaseUrl(path) + INVENTROY_CONSTANTS.ipPackageDetailRoute +
          base64UrlEncode(GetFirstElement(result?.ids)));
      }
    } else {
      error(result ? result?.errorMessage : INVENTORY_MESSAGE_CONSTANTS.errorOccurred);
    }
  };

  const getAllInclusions = (): string[] => {
    return inclusionMasterList
      .filter((x: any): boolean => x.isActive)
      .map((x: any): any => {
        return x.value;
      });
  };

  const onEditorContentChange = (editorText: any): any => {
    setEditorHtmlText(draftToHtml(editorText));
  };
  const onDiscountPercentChangeToAll = (discountPercent: any): void => {
    discountPercent = discountPercent === undefined ? 0 : discountPercent;
    const pricing = packageForm?.getFieldValue('pricingList');
    if (pricing && pricing?.length > 0) {
      pricing.map((val: any, i: number): void => {
        pricing[i].discountPercent = discountPercent;
        pricing[i].finalPrice = CalculateFinalPrice(Number(pricing[i].price || 0), Number(discountPercent || 0));
        packageForm?.setFieldsValue({ pricingList: pricing });
      });
      setDiscount(discountPercent);
    }
  };
  return (
    <div>
      <PageHeader
        backUrl={backUrl}
        pageTitle={pageTitle}
        rightComponent={
          (): ReactElement =>
            <Row gutter={GUTTOR_SPACE}>
              <Col md={18} />
              <Col md={6}>
                <div className="text-right">
                  <Button className="btn-form-submit" size={'middle'}
                    onClick={(): any => onFormSubmit()}> Submit
                  </Button>
                </div>
              </Col>
            </Row>}
      />
      <div className="ant-tabs-content-holder tab-custom-container"><Form
        {...layout}
        form={packageForm}
      >
        <Row gutter={GUTTOR_SPACE}>
          <Col md={16}>
            <Panel title={INVENTORY_MESSAGE_CONSTANTS.title.packageDetails}>
              <Row gutter={GUTTOR_SPACE}>
                <Col md={12}>
                  <Form.Item
                    label="Department"
                    name="department"
                    rules={[
                      {
                        required: true,
                        message: INVENTORY_MESSAGE_CONSTANTS.selectDepartment,
                      },
                    ]}
                  >
                    <Select
                      placeholder="Department name"
                      optionLabelProp="label"
                      allowClear={true}
                      showSearch={true}
                      options={departmentMasterList?.map((x: any): any => {
                        return {
                          name: x.value,
                          value: x.value,
                          key: x.id,
                        };
                      })}
                      onChange={onDepartmentChange}
                    />
                  </Form.Item>
                </Col>
                <Col md={12}>
                  <Form.Item
                    label="Procedure"
                    name="procedure"
                    rules={[
                      {
                        required: true,
                        message: INVENTORY_MESSAGE_CONSTANTS.selectProcedure,
                      },
                    ]}
                  >
                    <Select
                      placeholder="Procedure name"
                      optionLabelProp="label"
                      showSearch={true}
                      allowClear={true}
                      options={procedureList}
                      onChange={onProcedureChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={GUTTOR_SPACE}>
                <Col md={24}>
                  <Form.Item
                    label="Package description"
                    name="packageDescription"
                  >
                    <Editor
                      editorClassName="scroll-bar editor-style"
                      contentState={contentState}
                      onContentStateChange={onEditorContentChange}
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'fontFamily',
                          'list', 'textAlign', 'colorPicker', 'link', 'history'],
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={GUTTOR_SPACE}>
                <Col md={12}>
                  <Form.Item
                    label="Length of stay"
                    name="lengthOfStay"
                    className="mb-0"
                    rules={[
                      {
                        required: true,
                        message: INVENTORY_MESSAGE_CONSTANTS.enterLengthOfStay,
                      },
                      ({ getFieldValue }: any): any => ({
                        validator(rule: any, value: any): any {
                          const days = parseInt(replaceRsAndComma(getFieldValue('lengthOfStay')), 10);
                          if (!value || (days > 0 && days <= 90)) {
                            return Promise.resolve();
                          }
                          return Promise.reject('Enter valid length of stay');
                        },
                      }),
                    ]}
                  >
                    <NumberFormat
                      customInput={Input}
                      thousandSeparator={true}
                      thousandsGroupStyle="lakh"
                      autoComplete="off"
                      placeholder="Enter length of stay"
                      allowNegative={false}
                      decimalScale={0}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
            <Panel title={INVENTORY_MESSAGE_CONSTANTS.title.pricingDetails}
              extra={<Row gutter={GUTTOR_SPACE_SMALL}>
                <Col md={15} className="pr-0"><p className="label-title text-right" style={{ marginTop: 5 }}>
                  Discount for all rooms types</p></Col>
                <Col md={9}><Form.Item
                  name="discountAll"
                  className="mb-0"
                >
                  <Input type="number"
                    min="0"
                    max="100"
                    suffix="%" onChange={(e: any): any => onDiscountPercentChangeToAll(e?.target?.value)} />
                </Form.Item></Col>
              </Row>}>
              <Form.List name="pricingList">
                {(fields: any): any => {
                  return (<div>
                    {fields.map((field: any, index: number): any => (
                      <Row gutter={GUTTOR_SPACE} key={index} className={'custom-tooltip-row'}>
                        <Col md={8}>
                          <Form.Item
                            label="Room type"
                            name={[field.name, 'roomType']}
                            fieldKey={[field.fieldKey, 'roomType']}
                            tooltip={{
                              placement: 'rightTop',
                              overlayClassName: 'custom-tooltip-row-content',
                              icon: <InfoCircleOutlined />,
                              title: <div dangerouslySetInnerHTML=
                                {{ __html: getBulletPoints(packageForm.getFieldValue('pricingList')[index]?.rooms) }} />,
                            }}
                          >
                            <Input disabled={true} />
                          </Form.Item>
                        </Col>
                        <Col md={6}>
                          <Form.Item
                            label="Price"
                            name={[field.name, 'price']}
                            fieldKey={[field.fieldKey, 'price']}
                            rules={
                              [
                                {
                                  required: true,
                                  message: INVENTORY_MESSAGE_CONSTANTS.enterPrice,
                                },
                              ]}
                          >
                            <Input type="number" prefix="₹" min="0"
                              onChange={(e: any): any => onPriceChange(e?.target?.value, index)}
                            />
                          </Form.Item>
                        </Col>
                        <Col md={4}>
                          <Form.Item
                            label="Discount"
                            name={[field.name, 'discountPercent']}
                            fieldKey={[field.fieldKey, 'discountPercent']}
                          >
                            <Input type="number" max="100" min="0" suffix="%"
                              onChange={(e: any): any => onDiscountPercentChange(e?.target?.value, index)} />
                          </Form.Item>
                        </Col>
                        <Col md={6}>
                          <Form.Item
                            label="Discounted price"
                            name={[field.name, 'finalPrice']}
                            fieldKey={[field.fieldKey, 'finalPrice']}
                          >
                            <Input type="number"
                              prefix="₹" disabled={true} />
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                  </div>);
                }}
              </Form.List>
            </Panel>
          </Col>
          <Col md={8}>
            <Panel title="Inclusions">
              {inclusionMasterList &&
                <Form.Item
                  name="inclusions"
                >
                  <Checkbox.Group
                    className="inclusion-checkbox"
                    options={getAllInclusions()}
                  />
                </Form.Item>
              }
            </Panel>
          </Col>
        </Row>
      </Form>
      </div>
    </div >
  );
};

export default IPPackageForm;
